import { Injectable } from '@angular/core';
import { IApiBackendRouteService } from '../abstract/api-routes.service';
import { HttpClient, HttpParams } from '@angular/common/http';
import { API_BASE_URL } from '../../../core/providers/constants/api.constants';
import { Observable } from 'rxjs';
import { PackingBoxDto } from '../dto/packing-box.dto';
import { ApiRouteTransactionDto } from '../dto/purchase-order.dto';
import { PackingBoxDelete } from 'src/app/api/elite/dto/packing-box-delete.dto';

@Injectable({
  providedIn: 'root',
})
export class ApiRoutePackingBoxService extends IApiBackendRouteService {
  urlRoute = `${API_BASE_URL}/packing-box`;

  constructor(protected readonly http: HttpClient) {
    super();
  }

  softDeletePackingBoxes(ids: number[]): Observable<void> {
    return this.http.post<void>(`${this.urlRoute}/delete-request`, { ids });
  }

  approveSoftDeletePackingBoxes(ids: number[]): Observable<void> {
    return this.http.post<any>(`${this.urlRoute}/approve-deletion-request`, {
      ids,
    });
  }

  rejectSoftDeletePackingBoxes(ids: number[]): Observable<void> {
    return this.http.post<any>(`${this.urlRoute}/reject-deletion-request`, {
      ids,
    });
  }

  isPendingDeletion(id: number): Observable<boolean> {
    return this.http.get<boolean>(`${this.urlRoute}/${id}/is-pending`);
  }

  isApproved(id: number): Observable<boolean> {
    return this.http.get<boolean>(`${this.urlRoute}/${id}/deletion-approved`);
  }

  getDeletionInfo(id: number): Observable<{ deletedAt: Date | null }> {
    return this.http.get<{ deletedAt: Date | null }>(
      `${this.urlRoute}/${id}/deletion-info`
    );
  }

  getDeletionRequestInfo(id: number): Observable<PackingBoxDelete> {
    return this.http.get<PackingBoxDelete>(
      `${this.urlRoute}/${id}/deletion-request-info`
    );
  }

  traceabilityData(
    department: string
  ): Observable<PackingBoxDto['traceabilityData']['result']> {
    return this.http.get<PackingBoxDto['traceabilityData']['result']>(
      `${this.urlRoute}/traceability/data-by-department?department=${department}`
    );
  }

  traceabilityStageData(
    stage: string
  ): Observable<PackingBoxDto['traceabilityData']['result']> {
    return this.http.get<PackingBoxDto['traceabilityData']['result']>(
      `${this.urlRoute}/traceability/data-by-stage?stage=${stage}`
    );
  }

  createTraceabilityPackingBox(
    packingBox: PackingBoxDto['createTraceability']['body']
  ): Observable<PackingBoxDto['createTraceability']['response']> {
    return this.http.post<PackingBoxDto['createTraceability']['response']>(
      `${this.urlRoute}/traceability`,
      packingBox
    );
  }

  createBoxWithCobots(
    body: PackingBoxDto['createBoxWithCobots']['body']
  ): Observable<PackingBoxDto['createBoxWithCobots']['return']> {
    return this.http.post<PackingBoxDto['createBoxWithCobots']['return']>(
      `${this.urlRoute}/traceability/create-with-cobots`,
      body
    );
  }

  boxesAvailablesForCobots(
    data: PackingBoxDto['boxesAvailablesForCobots']['body']
  ): Observable<PackingBoxDto['boxesAvailablesForCobots']['return']> {
    const workOrderId = data.workOrderId;
    return this.http.get<PackingBoxDto['boxesAvailablesForCobots']['return']>(
      `${this.urlRoute}/traceability/availables-cobots/${workOrderId}`
    );
  }

  createTraceabilityPBStage(
    packingBox: PackingBoxDto['createTraceabilityStage']['body']
  ): Observable<number[]> {
    return this.http.post<number[]>(
      `${this.urlRoute}/traceability/stage`,
      packingBox
    );
  }

  legacyTraceabilityPackingBox(
    body: PackingBoxDto['legacyCreateTraceability']['body']
  ): Observable<number[]> {
    return this.http.post<number[]>(
      `${this.urlRoute}/traceability/legacy`,
      body
    );
  }

  deleteTraceabilityPackingBox(id: number | number[]): Observable<any> {
    if (Array.isArray(id)) {
      return this.http.delete(`${this.urlRoute}/traceability`, {
        body: { ids: id },
      });
    }
    return this.http.delete(`${this.urlRoute}/traceability/${id}`);
  }

  deleteTraceabilityPBStage(
    id: number | number[],
    stageName: string
  ): Observable<any> {
    return this.http.delete(`${this.urlRoute}/traceability/stage`, {
      body: { packingBoxIds: id, stageName },
    });
  }

  infoToDisplay(
    id: number
  ): Observable<PackingBoxDto['infoToDisplay']['response']> {
    return this.http.get<PackingBoxDto['infoToDisplay']['response']>(
      `${this.urlRoute}/${id}/info-to-display`
    );
  }
  infoToDisplayV2(
    id: number,
    addHistory: boolean = true
  ): Observable<PackingBoxDto['infoToDisplayV2']['response']> {
    let params = new HttpParams();

    params = params.append('addHistory', addHistory);

    return this.http.get<PackingBoxDto['infoToDisplayV2']['response']>(
      `${this.urlRoute}/${id}/info-to-display-v2`,
      {
        params,
      }
    );
  }

  createScanPackingBox(
    packingBox: PackingBoxDto['createScanBox']['body']
  ): Observable<PackingBoxDto['createScanBox']['return']> {
    return this.http.post<PackingBoxDto['createScanBox']['return']>(
      `${this.urlRoute}/traceability/scanner`,
      packingBox
    );
  }

  addToPallet(
    packingBoxId: number,
    body: {
      palletId: number;
      department?: string;
      upc?: string;
    }
  ): Observable<PackingBoxDto['infoToDisplay']['response']> {
    return this.http.patch<PackingBoxDto['infoToDisplay']['response']>(
      `${this.urlRoute}/${packingBoxId}/add-to-pallet`,
      {
        ...body,
        department: body.department || 'Packing Tracker',
      }
    );
  }

  removeFromPallet(
    packingBoxId: number,
    palletId: number
  ): Observable<PackingBoxDto['infoToDisplay']['response']> {
    return this.http.patch<PackingBoxDto['infoToDisplay']['response']>(
      `${this.urlRoute}/${packingBoxId}/remove-from-pallet`,
      {
        palletId,
      }
    );
  }

  dataToCreatePetition(
    department: string
  ): Observable<PackingBoxDto['dataToCreatePetition']['response']> {
    return this.http.get<PackingBoxDto['dataToCreatePetition']['response']>(
      `${this.urlRoute}/petition/data-to-create-petition?department=${department}`
    );
  }

  openPetitions(
    department: string
  ): Observable<PackingBoxDto['openPetitions']['response']> {
    return this.http.get<PackingBoxDto['openPetitions']['response']>(
      `${this.urlRoute}/petition/open?department=${department}`
    );
  }

  createPetition(
    body: PackingBoxDto['createPetition']['body']
  ): Observable<any> {
    return this.http.post(`${this.urlRoute}/petition`, body);
  }

  finishPetitionPackingBox(
    id: number
  ): Observable<PackingBoxDto['finishPackingBoxPetition']['response']> {
    return this.http.post<
      PackingBoxDto['finishPackingBoxPetition']['response']
    >(`${this.urlRoute}/petition/${id}/finish`, {});
  }

  validatePetition(
    id: number,
    salesOrder: ApiRouteTransactionDto['insert']['body']
  ): Observable<any> {
    return this.http.patch(`${this.urlRoute}/petition/${id}/validate`, {
      salesOrder,
    });
  }

  removeSeveralFromPallet(
    packingBoxIds: number[],
    palletId: number
  ): Observable<any> {
    return this.http.post<any>(`${this.urlRoute}/remove-from-pallet`, {
      palletId,
      packingBoxIds,
    });
  }

  clonePackingBox(
    packingBoxId: number,
    workOrderId: number
  ): Observable<PackingBoxDto['infoToDisplay']['response']> {
    return this.http.post<PackingBoxDto['infoToDisplay']['response']>(
      `${this.urlRoute}/traceability/clone`,
      {
        packingBoxId,
        workOrderId,
      }
    );
  }

  rebuildScrapPackingBox(
    body: PackingBoxDto['rebuildScrapPackingBox']['body']
  ): Observable<PackingBoxDto['rebuildScrapPackingBox']['return']> {
    return this.http.post<PackingBoxDto['rebuildScrapPackingBox']['return']>(
      `${this.urlRoute}/traceability/scrap`,
      {
        ...body,
      }
    );
  }

  rebuildPackingBoxes(
    body: PackingBoxDto['rebuildPackingBoxes']['body']
  ): Observable<PackingBoxDto['rebuildPackingBoxes']['return']> {
    return this.http.post<PackingBoxDto['rebuildPackingBoxes']['return']>(
      `${this.urlRoute}/traceability/rebuild`,
      {
        ...body,
      }
    );
  }

  lastScanPackingBox(
    teamId: number,
    workOrderId: number
  ): Observable<PackingBoxDto['infoToDisplay']['response']> {
    return this.http.get<PackingBoxDto['infoToDisplay']['response']>(
      `${this.urlRoute}/traceability/last-scan?teamId=${teamId}&workOrderId=${workOrderId}`
    );
  }

  scannerPackingBoxReport(
    queryString: string
  ): Observable<PackingBoxDto['scannedBoxReport']['result']> {
    return this.http.get<PackingBoxDto['scannedBoxReport']['result']>(
      `${this.urlRoute}/traceability/scanner/report?${queryString}`
    );
  }

  transformBoxesToDifferentSalesOrder(
    transactionId: number,
    boxesIds: number[]
  ): Observable<PackingBoxDto['infoToDisplayV2']['response'][]> {
    return this.http.post<PackingBoxDto['infoToDisplayV2']['response'][]>(
      `${this.urlRoute}/traceability/${transactionId}/transform-boxes`,
      {
        boxesIds,
      }
    );
  }

  rejectPettionforPackingBox(id: number): Observable<any> {
    return this.http.patch<any>(
      this.urlRoute + '/petition/' + id + '/reject',
      null
    );
  }

  getPackingBoxesByWorkOrder(
    department: string
  ): Observable<PackingBoxDto['workOrderDetails']['response']> {
    return this.http.get<any[]>(`${this.urlRoute}/work-order-details`, {
      params: { department },
    });
  }

  getPackingBoxesDetailsByWO(
    workOrder: string
  ): Observable<PackingBoxDto['packingBoxesDetailsByWO']['response']> {
    return this.http.get<any[]>(`${this.urlRoute}/packing-boxes-details`, {
      params: { workOrder },
    });
  }

  unbuildPackingBoxes(ids: number[]): Observable<void> {
    return this.http.patch<void>(`${this.urlRoute}/unbuild`, { ids });
  }
}
